import React, { useState } from 'react';
import axios from 'axios';
import { Send, Loader2 } from 'lucide-react';
import { toast } from '../hooks/use-toast';

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
  });
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);

    try {
      const payload = {
        name: formData.name.trim(),
        email: formData.email.trim(),
        message: formData.message.trim(),
      };
      if (formData.phone && formData.phone.trim()) {
        payload.phone = formData.phone.trim();
      }

      const res = await axios.post(`${API_BASE}/contact`, payload, {
        headers: { 'Content-Type': 'application/json' },
        timeout: 20000,
      });

      toast({
        title: 'Message sent',
        description: res?.data?.message || 'Thanks for reaching out. We will be in touch shortly.',
      });
      setFormData({ name: '', email: '', phone: '', message: '' });
    } catch (err) {
      const description =
        err?.response?.data?.detail ||
        err?.message ||
        'Something went wrong. Please try again or email info@reshadi.co.za directly.';
      toast({
        title: 'Could not send message',
        description,
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" className="relative py-16 md:py-24 lg:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        {/* Section Header */}
        <div className="mb-12 md:mb-16 lg:mb-20">
          <h2 className="text-5xl md:text-6xl lg:text-7xl font-bold text-[#3eb5a0] mb-4">
            Get In Touch
          </h2>
          <p className="text-lg md:text-xl text-[#2d2d2d]/70 font-light">Engage Reshadi on policy execution, B2G access, and sovereign infrastructure</p>
        </div>

        {/* Address */}
        <div className="mb-8 md:mb-10 max-w-3xl">
          <div className="border-l-2 border-[#3eb5a0] pl-5">
            <p className="text-xs uppercase tracking-[0.2em] text-[#2d2d2d]/50 font-medium mb-2">Address</p>
            <p className="text-base md:text-lg text-[#2d2d2d] font-light leading-relaxed">
              Road 6, Plot 75
              <br />
              Modder East Orchards
              <br />
              Delmas, 2211
              <br />
              Mpumalanga, South Africa
            </p>
          </div>
        </div>

        {/* Direct Contact — Leadership */}
        <div
          className="border-l-2 border-[#3eb5a0] pl-5 mb-12 md:mb-16 max-w-3xl"
          data-testid="contact-leadership"
        >
          <p className="text-xs uppercase tracking-[0.2em] text-[#2d2d2d]/50 font-medium mb-2">Direct Contact</p>
          <p className="text-lg md:text-xl text-[#2d2d2d] font-semibold">Tumi Serepo</p>
          <p className="text-sm md:text-base text-[#2d2d2d]/60 font-light mb-3">Chief Executive Officer</p>
          <div className="flex flex-col sm:flex-row sm:items-center sm:gap-6 gap-2 text-base md:text-lg font-light">
            <a
              href="mailto:tumi@reshadi.co.za"
              data-testid="contact-ceo-email"
              className="text-[#2d2d2d] hover:text-[#3eb5a0] transition-colors duration-300"
            >
              tumi@reshadi.co.za
            </a>
            <span aria-hidden="true" className="hidden sm:inline text-[#2d2d2d]/30">·</span>
            <a
              href="tel:+27815581869"
              data-testid="contact-ceo-phone"
              className="text-[#2d2d2d] hover:text-[#3eb5a0] transition-colors duration-300"
            >
              081 558 1869
            </a>
          </div>
        </div>

        {/* Contact Form */}
        <div className="max-w-3xl">
          <form onSubmit={handleSubmit} className="space-y-6 md:space-y-8" data-testid="contact-form">
            <div className="grid md:grid-cols-2 gap-6 md:gap-8">
              <div>
                <label htmlFor="name" className="block text-[#2d2d2d] font-medium mb-2 md:mb-3 text-base md:text-lg">
                  Full Name *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  data-testid="contact-name-input"
                  className="w-full px-0 py-2 md:py-3 bg-transparent border-b-2 border-[#2d2d2d]/20 text-[#2d2d2d] placeholder-[#2d2d2d]/40 focus:outline-none focus:border-[#3eb5a0] transition-colors duration-300 font-light text-base"
                  placeholder="Your name"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-[#2d2d2d] font-medium mb-2 md:mb-3 text-base md:text-lg">
                  Email Address *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  data-testid="contact-email-input"
                  className="w-full px-0 py-2 md:py-3 bg-transparent border-b-2 border-[#2d2d2d]/20 text-[#2d2d2d] placeholder-[#2d2d2d]/40 focus:outline-none focus:border-[#3eb5a0] transition-colors duration-300 font-light text-base"
                  placeholder="your@email.com"
                />
              </div>
            </div>

            <div>
              <label htmlFor="phone" className="block text-[#2d2d2d] font-medium mb-2 md:mb-3 text-base md:text-lg">
                Phone Number
              </label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                data-testid="contact-phone-input"
                className="w-full px-0 py-2 md:py-3 bg-transparent border-b-2 border-[#2d2d2d]/20 text-[#2d2d2d] placeholder-[#2d2d2d]/40 focus:outline-none focus:border-[#3eb5a0] transition-colors duration-300 font-light text-base"
                placeholder="+27 123 456 7890"
              />
            </div>

            <div>
              <label htmlFor="message" className="block text-[#2d2d2d] font-medium mb-2 md:mb-3 text-base md:text-lg">
                Message *
              </label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                required
                rows={5}
                data-testid="contact-message-input"
                className="w-full px-0 py-2 md:py-3 bg-transparent border-b-2 border-[#2d2d2d]/20 text-[#2d2d2d] placeholder-[#2d2d2d]/40 focus:outline-none focus:border-[#3eb5a0] transition-colors duration-300 resize-none font-light text-base"
                placeholder="Tell us about your project..."
              />
            </div>

            <button
              type="submit"
              disabled={submitting}
              data-testid="contact-submit-btn"
              className="px-8 md:px-10 py-3 md:py-4 bg-[#3eb5a0] text-white font-semibold rounded-full hover:bg-[#2d2d2d] transition-all duration-300 flex items-center gap-2 md:gap-3 text-base disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {submitting ? (
                <>
                  Sending…
                  <Loader2 size={18} className="animate-spin" />
                </>
              ) : (
                <>
                  Send Message
                  <Send size={18} />
                </>
              )}
            </button>
          </form>
        </div>
      </div>
    </section>
  );
};

export default Contact;
